/* ==========================================================================
   PT · 2024-kit adapter check — standalone.

   Deliberately shares NOTHING with the flagship matcher. That one speaks
   Standard/Slim, opens a body-locking sheet, fires GA4 pt_fit_match and writes
   cart attributes; none of that vocabulary or machinery applies here, and the
   flagship is days from launch. So this is its own file, its own ptml- prefix,
   and its own inline panel — no scroll lock, no focus trap, nothing to collide
   with. pt-page.js is not loaded on this section's behalf and is not touched.

   The question is simpler too. The 2024 kit ships a fixed set of adapters and
   legacy-adapter-data.json states, per phone, which size it needs: a numeric
   adapter_options_mm means the size is in the box, and no list at all means it
   is not — 76 of 254 phones, which is the whole reason the 2026 build exists.
   So the answer is binary and the widget asks one question, not two.
   ========================================================================== */
(function () {
  "use strict";

  var roots = document.querySelectorAll("[data-ptml]");
  if (!roots.length) return;

  roots.forEach(function (root) {
    if (root.__ptmlBound) return;
    root.__ptmlBound = true;

    var cfg = {
      src: root.getAttribute("data-src") || "",
      newUrl: root.getAttribute("data-new-url") || "",
      txtIn: root.getAttribute("data-txt-in") || "In this kit",
      txtOut: root.getAttribute("data-txt-out") || "Not in this kit",
      txtNewCta: root.getAttribute("data-txt-newcta") || "See the 2026 build",
      txtAgain: root.getAttribute("data-txt-again") || "Check another phone",
      txtContinue: root.getAttribute("data-txt-continue") || "Select your color",
      txtNoMatch: root.getAttribute("data-txt-nomatch") || "No match yet. Try another spelling, or contact us and we'll size it for you.",
      txtChat: root.getAttribute("data-txt-chat") || "Chat with us",
      headPhones: root.getAttribute("data-head-phones") || "Phones",
      headResults: root.getAttribute("data-head-results") || "Results"
    };

    var mask = document.getElementById("ptml-mask");
    var input = root.querySelector("[data-ptml-input]");
    var list = root.querySelector("[data-ptml-list]");
    var result = root.querySelector("[data-ptml-result]");
    var status = root.querySelector("[data-ptml-status]");
    var step1 = root.querySelector("[data-ptml-step1]");
    var listHead = root.querySelector(".pt-bh");
    if (!input || !list || !result) return;

    var phones = null, loading = false, open = false;
    var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function esc(s) {
      return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
      });
    }

    /* A phone is covered when the data gives it a numeric adapter size. The 76
       uncovered rows carry no adapter_options_mm at all rather than a sentinel,
       so absence is the signal — not a value to compare against. */
    function sizeFor(p) {
      var o = p && p.adapter_options_mm;
      if (!o || !o.length) return null;
      var n = Number(o[0]);
      return isFinite(n) ? n : null;
    }

    function norm(s) { return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, ""); }

    function matches(p, q) {
      if (!q) return true;
      var hay = [p.model, p.brand].concat(p.aliases || []);
      for (var i = 0; i < hay.length; i++) if (norm(hay[i]).indexOf(q) !== -1) return true;
      return norm(p.brand + " " + p.model).indexOf(q) !== -1;
    }

    function render(q) {
      if (!phones) return;
      var nq = norm(q);
      var rows = phones.filter(function (p) { return matches(p, nq); });
      rows.sort(function (a, b) { return (a.rank || 9999) - (b.rank || 9999); });
      /* Same shape as the flagship: a short prompt list before anyone types, a
         long one once they do, and the SHEET does the scrolling rather than an
         inner box with its own bar. */
      rows = rows.slice(0, nq ? 30 : 5);
      if (listHead) listHead.textContent = nq ? cfg.headResults : cfg.headPhones;
      if (!rows.length) {
        list.innerHTML = '<div class="ptml-nomatch">' + esc(cfg.txtNoMatch) + "</div>" +
          '<button type="button" class="pt-mcasebtn" data-ptml-chat>' + esc(cfg.txtChat) + "</button>";
        return;
      }
      /* One uppercase line of "brand model", exactly as the flagship's rows read
         — this is the lighter widget, not a different design. */
      list.innerHTML = rows.map(function (p, i) {
        return '<button type="button" class="ptml-row" data-i="' + i + '">' +
          (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy" decoding="async" width="40" height="40">' : '<span class="ptml-noimg"></span>') +
          '<span class="ptml-rowname">' + esc(p.brand + " " + p.model) + "</span></button>";
      }).join("");
      list.__rows = rows;
    }

    /* Back to the search, with the box cleared so the next phone starts fresh. */
    function reset() {
      result.hidden = true;
      if (step1) step1.hidden = false;
      if (listHead) listHead.hidden = false;
      input.value = "";
      render("");
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
    }

    function choose(p) {
      var mm = sizeFor(p);
      var covered = mm !== null;
      result.hidden = false;
      result.className = "ptml-result " + (covered ? "ptml-yes" : "ptml-no");
      var head = covered
        ? '<b>' + esc(cfg.txtIn) + "</b>"
        : '<b>' + esc(cfg.txtOut) + "</b>";
      var body = covered
        ? esc(p.model) + " uses the <b>" + mm.toFixed(1) + " mm</b> adapter, which ships in this kit."
        : esc(p.model) + " needs a size this kit does not include.";
      /* A yes needs somewhere to go. Without it the sheet ended on a fact and
         the shopper had to work out for themselves that the next move was to
         close it and find the button underneath. */
      var cta = covered
        ? '<button type="button" class="ptml-go" data-ptml-go>' + esc(cfg.txtContinue) + "</button>"
        : (cfg.newUrl ? '<a class="ptml-go" href="' + esc(cfg.newUrl) + '">' + esc(cfg.txtNewCta) + " &rarr;</a>" : "");
      result.innerHTML = '<span class="ptml-mark" aria-hidden="true">' + (covered ? "✓" : "!") + "</span>" +
        "<span class=\"ptml-rtx\">" + head + "<span>" + body + "</span></span>" + cta +
        '<button type="button" class="ptml-again" data-ptml-again>' + esc(cfg.txtAgain) + "</button>";
      /* The answer takes the sheet over rather than sitting under the list. */
      if (step1) step1.hidden = true;
      if (listHead) listHead.hidden = true;
      try { root.scrollTop = 0; } catch (e) {}
      if (status) status.textContent = (covered ? cfg.txtIn : cfg.txtOut) + ": " + p.model;
    }

    function load() {
      if (phones || loading || !cfg.src) return;
      loading = true;
      list.innerHTML = '<p class="ptml-none">Loading phones…</p>';
      fetch(cfg.src, { credentials: "omit" })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          phones = (d && d.phones) || [];
          loading = false;
          render(input.value);
        })
        .catch(function () {
          loading = false;
          list.innerHTML = '<p class="ptml-none">Could not load the phone list. The sizes are on the comparison table below.</p>';
        });
    }

    /* Hold the page still while the sheet is up, the same way the flagship does
       and reusing its .pt-sheet-lock rule — a sheet that lets the page scroll
       underneath drops the shopper somewhere else when it closes. The offset is
       restored on the way out. */
    var scrollY = 0, opener = null;

    function setOpen(v) {
      open = v;
      if (v) {
        opener = document.activeElement;
        scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        document.body.style.top = (-scrollY) + "px";
        document.body.classList.add("pt-sheet-lock");
        if (mask) mask.style.display = "block";
        root.style.display = "block";
        load();
        reset();
        /* lay the closed state out before the class that animates away from it,
           or the rise is skipped and the sheet blinks into place */
        if (!reduced) { void root.offsetHeight; if (mask) void mask.offsetHeight; }
        if (mask) mask.classList.add("pt-open");
        root.classList.add("pt-open");
        setTimeout(function () { try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); } }, reduced ? 40 : 240);
      } else {
        if (document.body.classList.contains("pt-sheet-lock")) {
          document.body.classList.remove("pt-sheet-lock");
          document.body.style.top = "";
          window.scrollTo(0, scrollY);
        }
        if (mask) mask.classList.remove("pt-open");
        root.classList.remove("pt-open");
        var hide = function () { if (mask) mask.style.display = "none"; root.style.display = "none"; };
        if (reduced) hide(); else setTimeout(hide, 340);
        if (opener && opener.focus) { try { opener.focus(); } catch (e) {} }
      }
    }

    /* The trigger lives in the buy box, a different section, so the opener is
       global — the same arrangement the flagship uses for ptOpenSheet. */
    window.ptmlOpen = function () { setOpen(true); };
    window.ptmlClose = function () { setOpen(false); };

    if (mask) mask.addEventListener("click", function () { setOpen(false); });
    input.addEventListener("input", function () { render(input.value); });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && open) setOpen(false);
    });
    result.addEventListener("click", function (e) {
      if (!e.target.closest) return;
      if (e.target.closest("[data-ptml-again]")) { reset(); return; }
      if (e.target.closest("[data-ptml-go]")) {
        setOpen(false);
        /* The colour picker, not Add to cart: choosing a colour is the step
           that actually comes next, and the button says so. */
        var buy = document.getElementById("pt-cs") || document.getElementById("pt-iatc") || document.getElementById("pt-buy");
        if (buy) setTimeout(function () {
          try { buy.scrollIntoView({ behavior: reduced ? "instant" : "smooth", block: "center" }); }
          catch (err) {}
        }, reduced ? 0 : 360);   /* after the sheet is out of the way */
      }
    });
    list.addEventListener("click", function (e) {
      if (e.target.closest && e.target.closest("[data-ptml-chat]")) {
        setOpen(false);
        if (window.ptOpenChat) window.ptOpenChat();
        return;
      }
      var b = e.target.closest && e.target.closest(".ptml-row");
      if (!b || !list.__rows) return;
      var p = list.__rows[Number(b.getAttribute("data-i"))];
      if (p) choose(p);
    });
  });
})();
